import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { addNativeAppListener } from "@/native/runtime";
import "./navigation.css";

const NavigationContext = createContext(null);
const entryFor = (location) => ({ key: location.key, pathname: location.pathname });
const isWorkspace = (path) => !["/login", "/recover-platform-owner", "/change-password"].includes(path);

// Only entries observed in this authenticated session are eligible for Back.
// Browser history length alone can include another website or a login screen.
export function updateHistory(history, location, action) {
  const found = history.entries.findIndex((entry) => entry.key === location.key);
  if (found >= 0) return { ...history, index: found };
  if (action === "POP") return { entries: [entryFor(location)], index: 0 };
  const entries = history.entries.slice(0, history.index + 1);
  if (action === "REPLACE" && entries.length) entries.pop();
  entries.push(entryFor(location));
  return { entries: entries.slice(-50), index: Math.min(entries.length - 1, 49) };
}

export function NavigationProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const action = useNavigationType();
  const scope = user && !user.must_change_password && !["/login", "/recover-platform-owner"].includes(location.pathname)
    ? `${user.id}:${user.company_id}:${user.platform_role}:${user.role}` : null;
  const memory = useRef(null);
  if (!memory.current || memory.current.scope !== scope) {
    memory.current = { scope, history: { entries: [], index: -1 }, pages: new Map() };
  }
  const session = memory.current;
  if (!session.pages.has(location.key)) session.pages.set(location.key, { values: new Map(), scroll: null });
  const page = session.pages.get(location.key);
  const history = updateHistory(session.history, location, action);
  const home = user?.platform_role === "platform_owner" ? "/platform/companies" : "/dashboard";
  const previous = history.entries[history.index - 1];
  const canReturn = scope && previous && isWorkspace(previous.pathname);
  const fallback = location.pathname.startsWith("/settings/") ? "/settings" : location.pathname.startsWith("/projects/") ? "/master-project-board" : home;
  const goBack = useCallback(() => {
    if (!scope) return;
    if (canReturn) navigate(-1);
    else if (location.pathname !== fallback) navigate(fallback, { replace: true });
  }, [scope, canReturn, navigate, location.pathname, fallback]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") return undefined;
    return addNativeAppListener("backButton", () => {
      const overlay = document.querySelector(
        '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"], ' +
        '[aria-modal="true"][role="dialog"], [aria-modal="true"][role="alertdialog"], ' +
        '[data-state="open"][role="menu"], [data-state="open"][role="listbox"]',
      );
      if (overlay) {
        document.dispatchEvent(new KeyboardEvent("keydown", {
          key: "Escape", code: "Escape", bubbles: true, cancelable: true,
        }));
        return;
      }
      if (scope && (canReturn || location.pathname !== fallback)) goBack();
      else App.minimizeApp().catch(() => {});
    });
  }, [scope, canReturn, location.pathname, fallback, goBack]);

  useLayoutEffect(() => {
    session.history = history;
    const liveKeys = new Set(history.entries.map((entry) => entry.key));
    for (const key of session.pages.keys()) if (!liveKeys.has(key)) session.pages.delete(key);
    const oldRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    let restoring = action === "POP" && !!page.scroll;
    const saved = page.scroll;
    const record = () => {
      if (restoring) return;
      page.scroll = {
        x: window.scrollX, y: window.scrollY,
        // Include horizontal board scrolling, not just the document position.
        boards: Array.from(document.querySelectorAll(".board-table-scroll")).map((element) => ({ x: element.scrollLeft, y: element.scrollTop })),
      };
    };
    const restore = () => {
      if (!restoring) return;
      window.scrollTo(saved.x, saved.y);
      document.querySelectorAll(".board-table-scroll").forEach((element, index) => {
        const position = saved.boards[index];
        if (position) { element.scrollLeft = position.x; element.scrollTop = position.y; }
      });
    };
    const stop = () => { restoring = false; observer.disconnect(); };
    const observer = new MutationObserver(restore);
    if (restoring) {
      restore();
      observer.observe(document.body, { childList: true, subtree: true });
    } else if (!location.hash) window.scrollTo(0, 0);
    // Wait for asynchronously loaded rows, but never fight deliberate scrolling.
    const timer = setTimeout(stop, 10000);
    window.addEventListener("scroll", record, true);
    window.addEventListener("wheel", stop, { passive: true });
    window.addEventListener("touchstart", stop, { passive: true });
    window.addEventListener("keydown", stop);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
      window.removeEventListener("scroll", record, true);
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchstart", stop);
      window.removeEventListener("keydown", stop);
      window.history.scrollRestoration = oldRestoration;
    };
    // Each history entry owns its UI snapshot. Do not restart for filter edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, scope]);

  return (
    <NavigationContext.Provider value={page}>
      {scope && (
        <nav className="jfm-back-bar" aria-label="Page navigation">
          <button type="button" onClick={goBack} disabled={!canReturn && location.pathname === fallback}
            className="jfm-back-button" data-testid="page-back-button" aria-label="Go back">
            <ArrowLeft size={18} aria-hidden="true" /> Back
          </button>
        </nav>
      )}
      {children}
    </NavigationContext.Provider>
  );
}

// Persist view choices only, never fetched records, credentials or form drafts.
// Memory is scoped to this login and the exact browser-history entry.
export function usePageState(name, initial) {
  const page = useContext(NavigationContext);
  const [local, setLocal] = useState(initial);
  const [, refresh] = useReducer((value) => value + 1, 0);
  if (page && !page.values.has(name)) page.values.set(name, typeof initial === "function" ? initial() : initial);
  const setValue = useCallback((next) => {
    if (!page) { setLocal(next); return; }
    const value = typeof next === "function" ? next(page.values.get(name)) : next;
    page.values.set(name, value);
    refresh();
  }, [name, page]);
  return [page ? page.values.get(name) : local, setValue];
}
